/* eslint-disable camelcase */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { createFilter } from 'react-select';
/* Utils */
import {
  getDeviceModelOptions,
  getDeviceMakeOptions,
  getSelectedModel,
  getModelQuestion,
  isStringType,
} from 'src/components/PreQuestion/utils';
/* Constants */
import {
  CUSTOM_DROPDOWN_OPTIONS,
  DEFAULT_VALUES,
  INITIAL_STATE,
} from 'src/components/PreQuestion/constants';
/* Components */
import { ErrorMsg } from 'src/components/HTKit/Forms/Parts';
import SelectField from 'src/components/HTKit/Forms/SelectField';
import Label from 'src/components/HTKit/Forms/Parts/Label/Label';
import { FORM_SIZE } from 'src/components/HTKit/Forms/constants';
import InputFieldV2 from 'src/components/HTKit/Forms/InputFieldV2';
import PreQuestionLabelWithHint from './PreQuestionLabelWithHint';

const PreQuestionDevice = ({
  question,
  value,
  modelValue,
  onMakeDropdownChange,
  onModelDropdownChange,
  onMakeInputChange,
  onModelInputChange,
  makeInputValue,
  modelInputValue,
  error,
  onIconClick,
  includeIDK = true,
  isEVFlow = false,
}) => {
  const filterOption = createFilter({});

  const questionAnswers = question.answers;
  let makeError = null;
  let modelError = null;
  if (error) {
    makeError = error.get('make');
    modelError = error.get('model');
  }
  // get options
  const options = getDeviceMakeOptions(question);
  const modelQuestion = getModelQuestion({ answers: questionAnswers });
  const hintText = question.hint;
  const [state, setState] = useState(INITIAL_STATE);
  const makeQuestion = question.textDirect;

  const {
    hintVisible,
    isModelDisabled,
    showInput,
    showModelInput,
    models,
    filteredModels,
    placeholder,
    allOptions,
  } = state;

  // push custom options 'other' & 'idk'
  options.push(CUSTOM_DROPDOWN_OPTIONS.OTHER);
  if (includeIDK) {
    options.push(CUSTOM_DROPDOWN_OPTIONS.I_DONT_KNOW);
  }

  useEffect(() => {
    const selected = getSelectedModel({ questionAnswers, valueId: value });
    const products = selected && selected.productQuestion;
    const modelOptions = getDeviceModelOptions({ products, includeIDK });
    setState((prevState) => {
      return {
        ...prevState,
        filteredModels: modelOptions,
        models: modelOptions,
        isModelDisabled: !value,
        allOptions: options,
        modelVal: modelValue,
      };
    });
    if (value === CUSTOM_DROPDOWN_OPTIONS.I_DONT_KNOW.value) {
      setState((prevState) => {
        return {
          ...prevState,
          isModelDisabled: true,
          showModelInput: false,
        };
      });
    }
    if (hintVisible) {
      setState((prevState) => {
        return {
          ...prevState,
          hintVisible: !hintVisible,
        };
      });
    }
  }, [value]);

  useEffect(() => {
    if (isStringType(modelValue)) {
      setState((prevState) => {
        return {
          ...prevState,
          showModelInput: true,
        };
      });
    }
    if (isStringType(value)) {
      setState((prevState) => {
        return {
          ...prevState,
          showInput: true,
          showModelInput: true,
        };
      });
    }
    if (value === CUSTOM_DROPDOWN_OPTIONS.I_DONT_KNOW.value) {
      setState((prevState) => {
        return {
          ...prevState,
          isModelDisabled: true,
          placeholder: CUSTOM_DROPDOWN_OPTIONS.I_DONT_KNOW.label,
        };
      });
    } else {
      setState((prevState) => {
        return {
          ...prevState,
          placeholder: DEFAULT_VALUES.PLACEHOLDER,
        };
      });
    }
  }, [value]);

  const handleMakeDropdownChange = (v) => {
    const isOtherOption = v === CUSTOM_DROPDOWN_OPTIONS.OTHER.value;
    const isIDKOption = v === CUSTOM_DROPDOWN_OPTIONS.I_DONT_KNOW.value;

    if (isOtherOption || isIDKOption) {
      if (isOtherOption) {
        setState((prevState) => {
          return {
            ...prevState,
            showModelInput: true,
            showInput: true,
            placeholder: CUSTOM_DROPDOWN_OPTIONS.OTHER.label,
          };
        });
      }
      if (isIDKOption) {
        setState((prevState) => {
          return {
            ...prevState,
            placeholder: CUSTOM_DROPDOWN_OPTIONS.I_DONT_KNOW.label,
            isModelDisabled: true,
          };
        });
      }
    } else {
      setState((prevState) => {
        return {
          ...prevState,
          isModelDisabled: false,
          showModelInput: false,
          showInput: false,
          placeholder: DEFAULT_VALUES.PLACEHOLDER,
        };
      });
    }
    const selected = getSelectedModel({ questionAnswers, valueId: v });
    const products = selected && selected.productQuestion;
    const modelOptions = getDeviceModelOptions({ products, includeIDK });
    setState((prevState) => {
      return {
        ...prevState,
        models: modelOptions,
      };
    });
    onMakeDropdownChange(v);
  };

  const handleModelDropdownChange = (v) => {
    const isOtherOption = v === CUSTOM_DROPDOWN_OPTIONS.OTHER.value;
    if (isOtherOption) {
      setState((prevState) => {
        return {
          ...prevState,
          showModelInput: true,
        };
      });
    }
    onModelDropdownChange(v);
  };

  const handleIconClick = () => {
    setState((prevState) => {
      return {
        ...prevState,
        showModelInput: false,
        showInput: false,
        isModelDisabled: true,
        placeholder: DEFAULT_VALUES.PLACEHOLDER,
      };
    });
    onIconClick();
  };

  /*
    * filterOption is a prop in react-select. Setting it to true disables the custom no options message and enables us
      to customize the options to be shown when no search results are found. (in type ahead)
    * Note that the no options message is pushed as a custom 'option' object where we can also add an 'isDisabled' prop that will make this non-interactive.
  */
  const filterAllOptions = (input, optionList, stateValue) => {
    const filteredOptions = optionList.filter(
      (o) => !(o.searchable === false) && filterOption(o, input),
    );
    const noOptions = filteredOptions.length === 0;
    if (noOptions) {
      filteredOptions.push(CUSTOM_DROPDOWN_OPTIONS.NO_OPTIONS);
    }
    filteredOptions.push(CUSTOM_DROPDOWN_OPTIONS.OTHER);
    if (includeIDK) {
      filteredOptions.push(CUSTOM_DROPDOWN_OPTIONS.I_DONT_KNOW);
    }

    setState((prevState) => {
      return {
        ...prevState,
        [stateValue]: filteredOptions,
      };
    });
  };
  const inputClassName = makeError ? 'marginTop-tiny1' : 'marginTop-small1';
  const makeQuestionLabel = hintText ? (
    <PreQuestionLabelWithHint label={makeQuestion} warning={hintText} />
  ) : (
    makeQuestion
  );

  /* Custom filter option for user filtering */
  const filterOptionFn = (...args) => {
    const [{label}, searchKey] = args;
    /* Lowercase the args */
    const lowerLabel = label?.toLowerCase() || '';
    const lowerSearchKey = searchKey?.toLowerCase() || '';
    /* Does the user input match the beginning of the option label */
    const filteredLabels = lowerLabel.startsWith(lowerSearchKey);
    /* We need to allow the custom options (i don't know, othe) to pass thru */
    const customOptions = Object.values(CUSTOM_DROPDOWN_OPTIONS).map(group => group.label.toLowerCase())

    /* We found a match or custom options */
    if (filteredLabels || customOptions.includes(lowerLabel)) {
      return true
    };

    return false;
  }

   /*
      If this is a non-editable device AND EV flow, return null;
   */
  if (question.nonEditable && isEVFlow) return null;

  return (
    <div className="positionRelative">
      <div>
        {showInput ? (
          <>
            <Label label={makeQuestionLabel} size={FORM_SIZE.medium} className="n700" />
            <InputFieldV2
              type="text"
              value={makeInputValue}
              onChange={onMakeInputChange}
              error={makeError}
              iconName="v2-close-icon"
              iconOnClick={handleIconClick}
              autoFocus={showInput}
              size={FORM_SIZE.medium}
              showError={false}
              errorClassName="flex justifyContentRight"
            />
          </>
        ) : (
          <>
            <Label label={makeQuestionLabel} size={FORM_SIZE.medium} className="n700" />
            <SelectField
              placeholder="Select"
              options={allOptions}
              filterOption={filterOptionFn}
              onInputChange={(input) => filterAllOptions(input, options, 'allOptions')}
              value={value}
              onChange={({ value: v }) => handleMakeDropdownChange(v)}
              size="md"
              isClearable={false}
              searchable
              error={makeError ? ' ' : null} // Use ErrorMsg to display text, but need to send error for CSS styles
            />
            {error && <ErrorMsg error={makeError} />}
          </>
        )}
      </div>
      <>
        {showModelInput ? (
          <div className={inputClassName}>
            <Label label={modelQuestion} size={FORM_SIZE.medium} className="n700" />
            <InputFieldV2
              type="text"
              value={modelInputValue}
              onChange={onModelInputChange}
              error={modelError}
              size={FORM_SIZE.medium}
              errorClassName="flex justifyContentRight"
              disabled={!value}
            />
          </div>
        ) : (
          <>
            <div className="marginTop-small1">
              <Label label={modelQuestion} size={FORM_SIZE.medium} className="n700" />
              <SelectField
                placeholder={placeholder}
                options={models}
                filterOption={filterOptionFn}
                onInputChange={(input) => filterAllOptions(input, filteredModels, 'models')}
                value={modelValue}
                isOptionSelected={Boolean(modelValue)}
                onChange={({ value: v }) => handleModelDropdownChange(v)}
                size="md"
                searchable
                isClearable={false}
                isDisabled={isModelDisabled}
                error={modelError ? ' ' : null} // Use ErrorMsg to display text, but need to send error for CSS styles
              />
            </div>
            {error && <ErrorMsg error={modelError} />}
          </>
        )}
      </>
    </div>
  );
};

PreQuestionDevice.propTypes = {
  question: PropTypes.object.isRequired,
  sku: PropTypes.object.isRequired,
  error: PropTypes.string,
  value: PropTypes.object,
  modelValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  makeInputValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  modelInputValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onMakeDropdownChange: PropTypes.func,
  onModelDropdownChange: PropTypes.func,
  onMakeInputChange: PropTypes.func,
  onModelInputChange: PropTypes.func,
  onIconClick: PropTypes.func,
  includeIDK: PropTypes.bool,
  isEVFlow: PropTypes.bool,
};

export default PreQuestionDevice;

/*
  --------------------------------
  NOTES
  --------------------------------
  Context:
    * There are two questions (currently) that a user answers - make, and, model of their device.
    * The questions are presented as dropdowns initially and if user picks "other" in make question,
      we render input fields for both questions.
    * We need to send back {answerId, make, model} for the questions to the BE (see newItem reducer)

  The question object coming back from the api looks something like this:

  questions:[
    0: {
      ...
      id: 5428
      inputType: "device"
      textDirect: "What MAKE is your computer?"
      text: "Apple"
      answers: [
        0: {
          ...
          id: 13272
          productQuestion: {
            id: 9
            text: "What MODEL is your computer?"
            answers: [
              0: {id: 24, name: "Apple TV"}
              1: {id: 135, name: "HomePod"}
              2: {id: 166, name: "Laptop"}
              3: {id: 167, name: "Desktop"}
            ]
          }
        1: {id: 13278, text: "Google"........}
      ]
    }
    1: {.....}
    2: {.....}
  ]
  - MR
*/
